import { Theme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import './assets/styles/main.scss';

import { ApolloProvider } from '@apollo/client';
import graphQLClient from './api/graphql/GraphQLClient';
import { Loader, NavBar, Snackbar } from './components';

import { Provider } from 'react-redux';
import store from './redux/configureStore';

import { BrowserRouter } from 'react-router-dom';
import LoginPage from './pages/login/index';
import AppRoutes from './routes/routes';

import { environment } from './environments/environment';
import { logger } from './utils/functionHelper';
import { UserDto } from './api';
import jwtDecode from 'jwt-decode';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import PasswordReset from './pages/passwordReset/PasswordReset';

export const GLOBAL_SESSION_STATE_NAMES = {
  VEHICLE_DEFINITIONS_PAGE_FILTER_ID: 'vehicleDefinitionsPageFilter',
  VEHICLE_CARDS_PAGE_FILTER_ID: 'vehicleCardsPageFilter',
  USED_VEHICLE_CARDS_PAGE_FILTER_ID: 'usedVehicleCardsPageFilter',
};

type Credentials = {
  username: string;
  password: string;
};

type UserClaims = {
  /**
   * Subject - unique identifier for the user - username
   */
  sub: string;
  name: string;
  surname: string;
  email: string;
  roles: string;
  /**
   * @type {UUID}
   */
  id: string;
};

function App(authKey) {
  const token = getToken();
  const [auth, setAuth] = useState(token);
  const [hasTempAccess, setHasTempAccess] = useState(false);

  async function loginUser(credentials) {
    let loginUrl = environment.loginEndpoint ? environment.loginEndpoint : '';
    let authBody: Credentials = { username: credentials.userName, password: credentials.password };

    if (environment.nodeEnv === 'development' && environment.mockedJWT) {
      setToken(environment.mockedJWT);
      setAuth(true);

      return Promise.resolve(true);
    } else {
      let myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');

      let requestOptions: RequestInit = {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(authBody),
        redirect: 'follow',
      };

      return fetch(loginUrl, requestOptions)
        .then((response) => {
          if (response.status !== 200) {
            logger('Authentication failed', response);
            throw new Error('Authentication failed');
          } else {
            return response;
          }
        })
        .then((response) => response.json())
        .then((data) => {
          setToken(data.jwt);
          setAuth(true);
          return true;
        })
        .catch((reason) => {
          logger(reason);
          setAuth(false);
          return false;
        })
        .finally(() => window.location.reload());
    }
  }

  useEffect(() => {
    if (token) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('tempAccess') && params.has('refresh') && !hasTempAccess) {
        setHasTempAccess(true);
      }
    }
  }, [token, hasTempAccess]);

  function setToken(userToken) {
    sessionStorage.setItem('token', JSON.stringify(userToken));
    const claims = jwtDecode<UserClaims>(userToken);
    const userDto: UserDto = new UserDto(
      claims.sub,
      claims.name,
      claims.surname,
      claims.email,
      claims.roles?.split(',').map((role) => role.trim()),
    );
    sessionStorage.setItem('user', JSON.stringify(userDto));
  }

  function getToken() {
    const params = new URLSearchParams(window.location.search);
    const tempAccess = params.get('tempAccess') || null;
    let tokenString = sessionStorage.getItem('token') || JSON.stringify(tempAccess);
    if (tokenString === null) {
      return false;
    }

    return JSON.parse(tokenString || '{}');
  }

  function userLogout() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    Object.keys(GLOBAL_SESSION_STATE_NAMES).forEach((key) =>
      sessionStorage.removeItem(GLOBAL_SESSION_STATE_NAMES[key]),
    );
    setAuth(false);
  }

  const theme: Theme = createTheme({
    palette: {
      primary: {
        main: '#000000',
      },
      secondary: {
        main: '#f2971e',
      },
    },
    components: {
      MuiGrid: {
        defaultProps: {
          spacing: 1,
        },
      },
    },
    typography: {
      fontSize: 14,
    },
  });

  const ContentComponent = !auth ? <LoginPage loginUser={loginUser} /> : <AppRoutes />;

  return (
    <ThemeProvider theme={theme}>
      <ApolloProvider
        client={graphQLClient}
        key={auth}
      >
        <Provider store={store}>
          <BrowserRouter>
            <NavBar userLogout={userLogout} />
            <div className='content'>
              <Snackbar />
              <Loader />
              {hasTempAccess ? <PasswordReset token={token} /> : ContentComponent}
            </div>
          </BrowserRouter>
        </Provider>
      </ApolloProvider>
    </ThemeProvider>
  );
}

export default App;
